import React, { Component, cloneElement } from 'react';

import classnames from 'classnames';
import { compose } from 'redux';

import { loadWidgetData } from 'common/actions/changelogWidget';
import AJAX from 'common/AJAX';
import CompanyContainer from 'common/containers/CompanyContainer';
import RouterContainer from 'common/containers/RouterContainer';
import SSOTokenContainer from 'common/containers/SSOTokenContainer';
import ThemeContainer from 'common/containers/ThemeContainer';
import TintColorContainer from 'common/containers/TintColorContainer';
import asyncConnect from 'common/core/asyncConnect';
import Helmet from 'common/helmets/Helmet';
import Message from 'common/message/Message';
import Spinner from 'common/Spinner';

import ChangelogWidgetFooter from './ChangelogWidgetFooter';
import ChangelogWidgetHeader from './ChangelogWidgetHeader';

import 'css/components/changelog/widget/_ChangelogWidgetContainer.scss';

export const UpdateHeightContext = React.createContext();

export const EntryListItemHeight = 96;
export const FooterHeight = 46;
export const HeaderHeight = 43;
// This is the default height if the user goes directly to entry details page rather than going via entry list items
// 2 pixels for changelogEntryListItem border-bottom, 2 pixels for changelogWidgetContainer borders
export const MinEntryHeight = 3 * EntryListItemHeight + FooterHeight + HeaderHeight + 4;

class ChangelogWidgetContainer extends Component {
  state = {
    fullscreenMode: false,
    navigatingBack: false,
    readIDs: null,
    ssoToken: this.props.location?.query?.ssoToken,
    theme: this.props.location?.query?.theme,
    labelIDs: this.props.location?.query?.labelIDs,
    desiredHeight: 0,
  };

  constructor(props, context) {
    super(props, context);

    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this._isMounted = true;
    this._unsubscribes = [
      Message.subscribe(window.parent, null, 'calculateHeight', this.onUpdateHeight),
      Message.subscribe(window.parent, null, 'entry-readIDs', (data) => {
        this.setState({
          readIDs: data.readIDs,
        });
      }),
      Message.subscribe(window.parent, null, 'set-fullscreen-mode', (data) => {
        this.setState({
          fullscreenMode: data.fullscreenMode,
        });
      }),
      Message.subscribe(window.parent, null, 'changelog-hidden', this.onWidgetHidden),
    ];

    this.onUpdateHeight();
    this.injectCookies();

    Message.postMessage(window.parent, '*', 'changelog-loaded', {
      entryIDs: this.mappedEntryIDs(),
    });

    if (!window.parent || window.parent === window) {
      this.setState({
        fullscreenMode: true,
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.params.entryURLName && !this.props.params.entryURLName) {
      this.onNavigateBackDone();
    }

    if (this.state.fullscreenMode !== prevState.fullscreenMode) {
      // skip updating height in this case, calculateHeight will get called directly
      // otherwise we get a weird animation
      return;
    } else if (this.state.desiredHeight !== prevState.desiredHeight) {
      // skip updating height because we just updated it
      return;
    }

    this.onUpdateHeight();
  }

  componentWillUnmount() {
    this._unsubscribes.forEach((unsubscribe) => {
      unsubscribe();
    });
  }

  mappedEntryIDs = () => {
    const { widgetData } = this.props;
    return (
      widgetData?.entries?.map((entry) => {
        return entry._id;
      }) || []
    );
  };

  injectCookies = () => {
    const {
      cookies,
      location: { query },
      params,
    } = this.props;
    AJAX.injectCookies({
      companyID: params.companyID,
      ...(query.ssoToken && {
        ssoToken: query.ssoToken,
      }),
      __canny__experimentID: cookies?.__canny__experimentID,
    });
  };

  // See SDK.js for detailed notes on closing the widget
  onWidgetHidden = () => {
    const router = this.props.router;
    const { companyID } = this.props.params;
    router.replace(`/${companyID}`);

    setTimeout(() => {
      Message.postMessage(window.parent, '*', 'close-changelog-cleanup');
    }, 0);
  };

  onCloseWidget = () => {
    Message.postMessage(window.parent, '*', 'close-changelog');
  };

  onEntryOpened = (entry) => {
    const { readIDs } = this.state;
    if (readIDs && readIDs.includes(entry._id)) {
      return;
    }

    this.setState({
      readIDs: (readIDs || []).concat(entry._id),
    });

    Message.postMessage(window.parent, '*', 'entry-opened', {
      entryID: entry._id,
    });
  };

  onNavigateBackDone = () => {
    this.setState({
      navigatingBack: false,
    });
  };

  onNavigateBackStart = () => {
    this.setState({
      navigatingBack: true,
    });
  };

  /**
   * How changelog widget width/height works:
   *
   * Fullscreen mode (widget is being opened in a mobile sized window)
   * - the widget's width and height are always the entire size of the screen
   *
   * Non-fullscreen
   * - the widget's width is fixed to 350px always
   *
   * Home View Height (non-fullscreen)
   * - the widget automatically resizes height to exactly fit its contents, no scrolling / extra spacing
   * - MinEntryHeight is defined as the height of the home view with 3 list items (1 line title, 2 lines details, each)
   * - NOTE: if we change text/spacing, we will likely need to update MinEntryHeight... we could make it smart and calculate?
   *
   * Entry View Height (non-fullscreen)
   * - the widget remembers the height of the home view (stored in desiredHeight)
   * - the height of the entry view is Math.max(MinEntryHeight, desiredHeight)
   * - so:
   *   - if home view is small, entry view height is MinEntryHeight
   *   - if home view is big, entry view height is the same as home view height
   *
   * Transitions
   * - if height needs to change between home and entry views, it should smoothly animate that with the SDK's animateHeight
   *
   * Window resize
   * - if height needs to change as a result of window resizes (eg. from desktop mode to mobile mode), it should snap, not animate
   * - this is what options.resize is for
   */
  onUpdateHeight = (options = { resize: false }) => {
    if (!this._isMounted) {
      return;
    }

    const container = this.containerRef.current;
    if (!container) {
      return;
    }

    const { params } = this.props;
    const { desiredHeight, fullscreenMode, navigatingBack } = this.state;
    const containerHeight = container.offsetHeight;
    const isViewingEntry = !!params.entryURLName;

    let height; // sorry niall
    if (isViewingEntry) {
      if (navigatingBack && !fullscreenMode) {
        height = desiredHeight;
      } else {
        height = Math.max(desiredHeight, MinEntryHeight);
      }
    } else {
      height = containerHeight;
    }

    if (height === this._lastHeight) {
      // height doesn't need update
      return;
    }

    if (!window.parent) {
      return;
    }

    if (height === 0) {
      // changelog is closed, do not propagate height update
      return;
    }

    if (!options.resize && !isViewingEntry && !fullscreenMode && desiredHeight !== height) {
      this.setState({
        desiredHeight: height,
      });
    }

    Message.postMessage(window.parent, '*', 'changelog-height', {
      height,
      resize: options.resize,
    });
  };

  renderContents() {
    const { widgetData } = this.props;
    if (!widgetData) {
      return (
        <div className="changelogWidgetContainer" ref={this.containerRef}>
          <Spinner />
        </div>
      );
    }

    if (widgetData.error) {
      if (widgetData.error === 'changelog is private') {
        return (
          <div className="changelogWidgetContainer" ref={this.containerRef}>
            <div className="error">This changelog is&nbsp;private.</div>
          </div>
        );
      }

      return (
        <div className="changelogWidgetContainer" ref={this.containerRef}>
          <div className="error">Something went wrong, please try again&nbsp;later.</div>
        </div>
      );
    }

    if (widgetData.loading || widgetData.company.loading) {
      return (
        <div className="changelogWidgetContainer" ref={this.containerRef}>
          <Spinner />
        </div>
      );
    }

    const { params } = this.props;
    const { desiredHeight, fullscreenMode, labelIDs, navigatingBack, readIDs, ssoToken } =
      this.state;
    const isViewingEntry = params.entryURLName;
    const { company } = widgetData;
    const { tintColor } = company;
    const changelog = {
      hasRSSFeed: widgetData.hasRSSFeed,
      labels: widgetData.labels,
    };
    const scrollContainerOverflowY = fullscreenMode ? 'auto' : isViewingEntry ? 'auto' : 'hidden';
    const containerMaxHeight = fullscreenMode
      ? '100%'
      : isViewingEntry
        ? Math.max(MinEntryHeight, desiredHeight)
        : null;
    // needsStretch is the case where we detect that entry view height is larger than its contents
    // in this case, we need to ensure ChangelogWidgetEntry takes up the whole space so we don't have a bunch of empty space below the footer
    // instead, a position: sticky footer with bottom: 0 might do the trick too.
    const needsStretch = !fullscreenMode && isViewingEntry && desiredHeight < containerMaxHeight;
    return (
      <UpdateHeightContext.Provider value={this.onUpdateHeight}>
        <RouterContainer
          location={this.props.location}
          params={this.props.params}
          route={this.props.route}
          router={this.props.router}>
          <CompanyContainer company={company}>
            <TintColorContainer tintColor={tintColor}>
              <SSOTokenContainer ssoToken={ssoToken}>
                <div
                  className={classnames('changelogWidgetContainer', {
                    fullscreenMode,
                    stretch: needsStretch,
                  })}
                  ref={this.containerRef}
                  style={{
                    maxHeight: containerMaxHeight,
                  }}>
                  <Helmet title="Changelog Widget" />
                  <ChangelogWidgetHeader
                    navigatingBack={navigatingBack}
                    onDismiss={this.onCloseWidget}
                    onNavigateBackStart={this.onNavigateBackStart}
                    showDismissButton={fullscreenMode}
                  />
                  <div
                    className="scrollContainer"
                    style={{
                      overflowY: scrollContainerOverflowY,
                    }}>
                    {cloneElement(this.props.children, {
                      changelog: changelog,
                      entries: widgetData.entries,
                      fullscreenMode,
                      isNavigatingBack: navigatingBack,
                      onEntryOpened: this.onEntryOpened,
                      readIDs: readIDs,
                      ssoToken: ssoToken,
                    })}
                  </div>
                  <ChangelogWidgetFooter
                    changelog={changelog}
                    fullscreenMode={fullscreenMode}
                    labelIDs={labelIDs ? JSON.parse(labelIDs) : []}
                  />
                </div>
              </SSOTokenContainer>
            </TintColorContainer>
          </CompanyContainer>
        </RouterContainer>
      </UpdateHeightContext.Provider>
    );
  }

  render() {
    const { theme } = this.state;
    return <ThemeContainer configTheme={theme}>{this.renderContents()}</ThemeContainer>;
  }
}

export default compose(
  asyncConnect(
    [
      {
        promise: ({ store: { dispatch }, location: { query }, params }) => {
          let labelIDs;
          try {
            labelIDs = query.labelIDs ? JSON.parse(query.labelIDs) : [];
          } catch (e) {
            labelIDs = [];
          }

          return dispatch(loadWidgetData(params.companyID, query.ssoToken, labelIDs));
        },
      },
    ],
    (state) => ({ widgetData: state.changelogWidget, cookies: state.cookies }),
  ),
)(ChangelogWidgetContainer);
